import {isNil, isNotNil} from 'ramda';

import type {DailyStockTakeDetailResponse} from '@dms/api/reporting';

import {type Nullish, parseDate} from 'shared/web';

const PERCENT = 100;

export interface DailyStockTakeMoney {
  value: number;
  currency: string;
}

/**
 * Identifies a line of the detail. The dialog maps it to a translated label, so the builder
 * stays free of i18n and can be asserted on directly.
 */
export type DailyStockTakeItemId =
  | 'dailyReceipts'
  | 'dailyIssues'
  | 'corrections'
  | 'receiptsFromSuppliers'
  | 'transfersToWarehouse'
  | 'materialReturns'
  | 'issuesToOrders'
  | 'transfersFromWarehouse';

export interface DailyStockTakeItem {
  id: DailyStockTakeItemId;
  amount: DailyStockTakeMoney | null;
  /** Number of receive/issue notes behind the amount, when the closing job reports one. */
  documents: number | null;
  /**
   * Number of stock cards those notes touched. A card moved by two lines counts in both, so
   * these do not add up to the day's card count shown under the total.
   */
  articles: number | null;
}

export interface DailyStockTakeDetailView {
  warehouseLabel: string | null;
  branchLabel: string | null;
  closingDay: Date | null;
  /** Day the opening balance was carried over from, when the job reported a usable one. */
  openingSourceDay: Date | null;
  hasDayGap: boolean;
  opening: {amount: DailyStockTakeMoney | null; articles: number | null};
  closing: {amount: DailyStockTakeMoney | null; articles: number | null};
  difference: DailyStockTakeMoney | null;
  /** Difference as a share of the opening value, in percent. Null when it cannot be derived. */
  differencePercent: number | null;
  checkItems: DailyStockTakeItem[];
  checkSum: DailyStockTakeMoney | null;
  isCheckCorrect: boolean | null;
  articlesCheckFailed: number | null;
  /** Whether the closing job found cards the check could not reconcile. A reported 0 is not one. */
  hasFailedArticles: boolean;
  receiptItems: DailyStockTakeItem[];
  receiptTotal: DailyStockTakeMoney | null;
  /**
   * Cards touched by the day's receipts. The closing job reports this for the day as a whole,
   * not per breakdown line, so it belongs to the total rather than to a line.
   */
  receiptArticles: number | null;
  issueItems: DailyStockTakeItem[];
  issueTotal: DailyStockTakeMoney | null;
  /** Cards touched by the day's issues — a day figure, like `receiptArticles`. */
  issueArticles: number | null;
  /**
   * The corrections column. The closing job reports the day's corrections as one figure and
   * exposes none of the parts it is made of, so this column is a total with no lines under it.
   * `corrPriorPeriodValue` is deliberately not used here: the job documents it as a subset of
   * material returns, already counted inside the day's receipts, not inside its corrections.
   */
  correctionTotal: DailyStockTakeMoney | null;
  /** Completed receive note correction documents for the day. */
  correctionDocuments: number | null;
  /** Receive note corrections still in progress — one figure, like the completed column. */
  inProgressCorrectionTotal: DailyStockTakeMoney | null;
  inProgressReceiptItems: DailyStockTakeItem[];
  inProgressReceiptTotal: DailyStockTakeMoney | null;
  inProgressIssueItems: DailyStockTakeItem[];
  inProgressIssueTotal: DailyStockTakeMoney | null;
}

function toCount(count: number | Nullish): number | null {
  return isNil(count) || !Number.isFinite(count) ? null : count;
}

/**
 * An unparseable day is dropped rather than passed on: the locale formatter throws
 * `RangeError: Invalid time value` on an invalid Date, which during render would take down the
 * whole dialog over one bad field.
 */
function toDay(value: string | Nullish): Date | null {
  if (isNil(value)) {
    return null;
  }

  const parsed: Date | null = parseDate(value);

  return isNotNil(parsed) && Number.isFinite(parsed.getTime()) ? parsed : null;
}

/**
 * The difference as a share of the opening value, in percent.
 *
 * Derived from the raw figures rather than from the formatted amounts, because a ratio needs no
 * currency. The opening value is taken as a magnitude so a negative opening cannot flip the sign
 * against the difference it describes, and the quotient is re-checked for overflow: an opening
 * value close to zero can push it past the float range, and the number formatter renders an
 * infinite value as text rather than rejecting it.
 */
function toPercentOfOpening(
  difference: number | Nullish,
  opening: number | Nullish
): number | null {
  const isUsable =
    isNotNil(difference) &&
    isNotNil(opening) &&
    Number.isFinite(difference) &&
    Number.isFinite(opening) &&
    opening !== 0;

  if (!isUsable) {
    return null;
  }

  const percent = (difference / Math.abs(opening)) * PERCENT;

  return Number.isFinite(percent) ? percent : null;
}

/**
 * Turns the fetched row into the shape the detail dialog renders.
 *
 * Amounts are taken from the closing job rather than recomputed — the job is the source of
 * truth for the daily closing, and recomputing here would hide a disagreement instead of
 * surfacing it. Historical snapshots may report pending returns without including them in the
 * receipt total. The only derived number is the percentage change, which the response omits.
 */
export function buildDailyStockTakeDetailView(
  detail: DailyStockTakeDetailResponse,
  currency: string | Nullish
): DailyStockTakeDetailView {
  // Without a currency an amount cannot be rendered as money, so it is reported as absent
  // rather than as a bare number the user could mistake for a document count. This covers a
  // missing currency only — a currency code the locale layer does not know still renders
  // without a symbol, which is platform behaviour rather than something decided here.
  const toMoney = (value: number | Nullish): DailyStockTakeMoney | null =>
    isNil(value) || !Number.isFinite(value) || isNil(currency) ? null : {value, currency};

  const toItem = (
    id: DailyStockTakeItemId,
    amount: number | Nullish,
    count?: number | Nullish,
    articles?: number | Nullish
  ): DailyStockTakeItem => ({
    id,
    amount: toMoney(amount),
    documents: toCount(count),
    articles: toCount(articles),
  });

  const opening = toMoney(detail.openingInventoryValue);
  const difference = toMoney(detail.inventoryValueDifference);
  const articlesCheckFailed = toCount(detail.articlesCheckFailed);

  return {
    warehouseLabel: detail.warehouseName ?? null,
    branchLabel: detail.branchName ?? null,
    closingDay: toDay(detail.closingDay),
    openingSourceDay: toDay(detail.openingSourceDay),
    hasDayGap: detail.hasDayGap === true,
    opening: {amount: opening, articles: toCount(detail.articlesWithValueStart)},
    closing: {
      amount: toMoney(detail.closingInventoryValue),
      articles: toCount(detail.articlesWithValueEnd),
    },
    difference,
    // A zero opening value has no meaningful percentage change, so it stays unreported.
    differencePercent: toPercentOfOpening(
      detail.inventoryValueDifference,
      detail.openingInventoryValue
    ),
    checkItems: [
      toItem('dailyReceipts', detail.dailyReceiptsValue, detail.receiptedArticlesCount),
      toItem('dailyIssues', detail.dailyIssuesValue, detail.issuedArticlesCount),
      toItem('corrections', detail.dailyCorrectionsValue),
    ],
    checkSum: toMoney(detail.checkSum),
    isCheckCorrect: detail.checkIsCorrect ?? null,
    articlesCheckFailed,
    hasFailedArticles: isNotNil(articlesCheckFailed) && articlesCheckFailed !== 0,
    receiptItems: [
      toItem(
        'receiptsFromSuppliers',
        detail.receiptsFromSuppliers,
        detail.receiptsFromSuppliersCount,
        detail.receiptsFromSuppliersArticlesCount
      ),
      toItem(
        'transfersToWarehouse',
        detail.transferToWarehouse,
        detail.transferToWarehouseCount,
        detail.transferToWarehouseArticlesCount
      ),
      toItem(
        'materialReturns',
        detail.materialReturns,
        detail.materialReturnsCount,
        detail.materialReturnsArticlesCount
      ),
    ],
    receiptTotal: toMoney(detail.dailyReceiptsValue),
    receiptArticles: toCount(detail.receiptedArticlesCount),
    issueItems: [
      toItem(
        'issuesToOrders',
        detail.issuancesToOrders,
        detail.issueNotesToOrdersCount,
        detail.issuancesToOrdersArticlesCount
      ),
      toItem(
        'transfersFromWarehouse',
        detail.transferFromWarehouse,
        detail.transferFromWarehouseCount,
        detail.transferFromWarehouseArticlesCount
      ),
    ],
    issueTotal: toMoney(detail.dailyIssuesValue),
    issueArticles: toCount(detail.issuedArticlesCount),
    correctionTotal: toMoney(detail.dailyCorrectionsValue),
    correctionDocuments: toCount(detail.numberOfCorrections),
    inProgressCorrectionTotal: toMoney(detail.pendingCorrections),
    /**
     * The work-in-progress tab mirrors the completed one, so its lines carry the same labels.
     * Pending material returns have their own amount alongside purchases and transfers.
     * The tab has no difference: it would set goods not yet received against goods not yet
     * issued, which is not a stock movement.
     */
    inProgressReceiptItems: [
      toItem('receiptsFromSuppliers', detail.pendingGoodsPurchase),
      toItem('transfersToWarehouse', detail.pendingGoodsTransfer),
      toItem('materialReturns', detail.pendingReturns),
    ],
    inProgressReceiptTotal: toMoney(detail.receiptNoteInProgress),
    inProgressIssueItems: [
      toItem('issuesToOrders', detail.pendingIssuesToOrders),
      toItem('transfersFromWarehouse', detail.pendingTransferFromWarehouse),
    ],
    inProgressIssueTotal: toMoney(detail.pendingIssues),
  };
}
